
import { useState, useEffect } from "react";
import {
  Settings,
  Columns,
  Filter,
  Search,
  Trash2,
  Group,
  Ungroup,
  Plus,
  RefreshCw
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { ColumnOption } from "../ColumnSelector";
import { ColumnSelector } from "../ColumnSelector";
import { ViewSwitcher, ViewType } from "../ViewSwitcher";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { Contract, ContractGroup } from "@/lib/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useContractGroups } from "@/hooks/useContractGroups";
import { useTranslation } from "react-i18next";
import { TranslationInput } from "@/components/i18n/TranslationInput";
import { TranslationButton } from "@/components/i18n/TranslationButton";
import { useTranslationEdit } from "@/contexts/TranslationEditContext";
import { FeatureGuard } from "@/components/common/FeatureGuard";

interface TableActionsProps {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  resetFilters: () => void;
  hasActiveFilters: () => boolean;
  selectedContracts: Set<string>;
  setSelectedContracts: (selectedContracts: Set<string>) => void;
  handleBulkDelete: () => void;
  availableColumns: ColumnOption[];
  selectedColumns: string[];
  onColumnChange: (columns: string[]) => void;
  viewType: ViewType;
  onViewChange?: (view: ViewType) => void;
  onGroupContracts: (contractIds: string[], groupId: string) => Promise<void>;
  onRemoveFromGroup: (contractIds: string[]) => Promise<void>;
  contractGroups: ContractGroup[];
  filters: any;
  toggleFilter: (filterType: keyof any, value: string | number | { start: Date | null; end: Date | null }) => void;
  isAdmin?: boolean;
  onUpdateAllRates?: () => Promise<void>;
  isUpdatingRates?: boolean;
  canUseGroupedContracts?: boolean;
}

export function TableActions({
  searchTerm,
  setSearchTerm,
  resetFilters,
  hasActiveFilters,
  selectedContracts,
  setSelectedContracts,
  handleBulkDelete,
  availableColumns,
  selectedColumns,
  onColumnChange,
  viewType,
  onViewChange,
  onGroupContracts,
  onRemoveFromGroup,
  contractGroups,
  filters,
  toggleFilter,
  isAdmin,
  onUpdateAllRates,
  isUpdatingRates,
  canUseGroupedContracts = false
}: TableActionsProps) {
  const { t } = useTranslation();
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const { useUserPreferencesQuery, updatePreferences } = useUserPreferences();
  const { data: userPreferences } = useUserPreferencesQuery();
  const [showGrouped, setShowGrouped] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("existing");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const { isEditMode } = useTranslationEdit();
  
  const { useCreateContractGroupMutation } = useContractGroups();
  const createGroupMutation = useCreateContractGroupMutation();
  
  useEffect(() => {
    if (userPreferences) {
      setShowGrouped(userPreferences.showGroupedContracts !== false);
    }
  }, [userPreferences]);
  
  const handleShowGroupedChange = async (value: boolean) => {
    setShowGrouped(value);
    try {
      await updatePreferences({ showGroupedContracts: value });
      toast.success(`Grouped contracts display ${value ? 'enabled' : 'disabled'}`);
      window.location.reload();
    } catch (error) {
      console.error("Failed to update grouping preference:", error);
      toast.error("Failed to save your preference");
    }
  };

  const handleGroupSelectionChange = (value: string) => {
    setSelectedGroupId(value === "none" ? null : value);
  };

  const handleAssignToExistingGroup = async () => {
    if (selectedContracts.size === 0) {
      toast.error("No contracts selected");
      return;
    }

    if (!selectedGroupId) {
      toast.error("No group selected");
      return;
    }

    const contractIds = Array.from(selectedContracts);
    try {
      await onGroupContracts(contractIds, selectedGroupId);
      toast.success("Contracts assigned to group");
      setGroupDialogOpen(false);
      setSelectedGroupId(null);
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error assigning contracts to group:", error);
      toast.error("Failed to assign contracts to group");
    }
  };

  const handleRemoveFromGroupAction = async () => {
    if (selectedContracts.size === 0) {
      toast.error("No contracts selected");
      return;
    }

    const contractIds = Array.from(selectedContracts);
    try {
      await onRemoveFromGroup(contractIds);
      toast.success("Contracts removed from group");
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error removing contracts from group:", error);
      toast.error("Failed to remove contracts from group");
    }
  };

  const handleCreateAndAssignGroup = async () => {
    if (selectedContracts.size === 0) {
      toast.error("No contracts selected");
      return;
    }

    if (!newGroupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      const newGroup = await createGroupMutation.mutateAsync({
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || ""
      });

      const contractIds = Array.from(selectedContracts);
      await onGroupContracts(contractIds, newGroup.id);
      
      toast.success("New group created and contracts assigned");
      setGroupDialogOpen(false);
      resetNewGroupForm();
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error creating group and assigning contracts:", error);
      toast.error("Failed to create group and assign contracts");
    }
  };

  const openGroupDialog = () => {
    setSelectedGroupId(null);
    setActiveTab("existing");
    resetNewGroupForm();
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setSelectedGroupId(null);
    resetNewGroupForm();
  };

  const resetNewGroupForm = () => {
    setNewGroupName("");
    setNewGroupDescription("");
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between gap-3">
      <div className="flex flex-col sm:flex-row items-center gap-2">
        <div className="relative flex items-center w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <TranslationInput
            type="search"
            i18nKey="searchContracts"
            placeholderText={t('searchContracts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 max-w-xs sm:w-auto"
          />
        </div>
        {(
          <TranslationButton
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            disabled={!hasActiveFilters() && !isEditMode}
            i18nKey="resetFilters"
          >
            <Filter className="h-4 w-4 mr-2" />
            {t('resetFilters')}
          </TranslationButton>
        )}
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        {selectedContracts.size > 0 && (
          <div className="flex items-center gap-2 mr-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <TranslationButton variant="destructive" size="sm" i18nKey="delete">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('delete')} ({selectedContracts.size})
                </TranslationButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('deleteContractsWarning')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete}>{t('continue')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            {canUseGroupedContracts && (
              <>
                <TranslationButton 
                  variant="outline" 
                  size="sm" 
                  onClick={openGroupDialog}
                  disabled={isEditMode}
                  i18nKey="assignToGroup"
                >
                  <Group className="h-4 w-4 mr-2" />
                  {t('assignToGroup')}
                </TranslationButton>
                
                <TranslationButton 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRemoveFromGroupAction}
                  disabled={isEditMode}
                  i18nKey="removeFromGroup"
                >
                  <Ungroup className="h-4 w-4 mr-2" />
                  {t('removeFromGroup')}
                </TranslationButton>
              </>
            )}
            
            {!canUseGroupedContracts && (
              <FeatureGuard feature="grouped_contracts" inline>
                <div className="flex items-center gap-2">
                  <TranslationButton 
                    variant="outline" 
                    size="sm" 
                    disabled={true}
                    i18nKey="assignToGroup"
                  >
                    <Group className="h-4 w-4 mr-2" />
                    {t('assignToGroup')}
                  </TranslationButton>
                  
                  <TranslationButton 
                    variant="outline" 
                    size="sm" 
                    disabled={true}
                    i18nKey="removeFromGroup"
                  >
                    <Ungroup className="h-4 w-4 mr-2" />
                    {t('removeFromGroup')}
                  </TranslationButton>
                </div>
              </FeatureGuard>
            )}
          </div>
        )}
        
        {onViewChange && (
          <ViewSwitcher 
            currentView={viewType} 
            onViewChange={isEditMode ? undefined : onViewChange}
          />
        )}
        
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>
            <TranslationButton
              variant="outline"
              size="sm"
              className="hidden sm:flex"
              i18nKey="settings"
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('settings')}
            </TranslationButton>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">{t('tableSettings')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('configureContractsDisplay')}
                </p>
              </div>
              <Separator />
              <div className="space-y-4">
                {canUseGroupedContracts && (
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col space-y-1">
                      <Label htmlFor="show-grouped">{t('showGroupedContracts')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('displayContractsInGroups')}
                      </p>
                    </div>
                    <Switch 
                      id="show-grouped" 
                      checked={showGrouped}
                      onCheckedChange={handleShowGroupedChange}
                    />
                  </div>
                )}
                
                {!canUseGroupedContracts && (
                  <FeatureGuard feature="grouped_contracts">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col space-y-1">
                        <Label htmlFor="show-grouped">{t('showGroupedContracts')}</Label>
                        <p className="text-xs text-muted-foreground">
                          {t('displayContractsInGroups')}
                        </p>
                      </div>
                      <Switch 
                        id="show-grouped" 
                        disabled={true}
                        checked={false}
                      />
                    </div>
                  </FeatureGuard>
                )}
                
                <div>
                  <Label htmlFor="columns">{t('columns')}</Label>
                  <Button 
                    id="columns"
                    onClick={() => setColumnSelectorOpen(true)} 
                    variant="outline" 
                    className="w-full justify-start mt-1"
                  >
                    <Columns className="mr-2 h-4 w-4" /> 
                    {t('customizeColumns')}
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        {isAdmin && onUpdateAllRates && (
          <TranslationButton 
            variant="outline" 
            size="sm"
            onClick={onUpdateAllRates}
            disabled={isUpdatingRates}
            i18nKey="updateAllRates"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isUpdatingRates ? 'animate-spin' : ''}`} />
            {isUpdatingRates ? t('updating') : t('updateAllRates')}
          </TranslationButton>
        )}
      </div>
      
      {columnSelectorOpen && (
        <ColumnSelector 
          availableColumns={availableColumns}
          selectedColumns={selectedColumns}
          onColumnChange={onColumnChange}
          open={columnSelectorOpen}
          onOpenChange={setColumnSelectorOpen}
        />
      )}
      
      <Dialog open={groupDialogOpen && canUseGroupedContracts} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('assignToGroup')}</DialogTitle>
            <DialogDescription>
              {t('selectOrCreateGroup')}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="existing" value={activeTab} onValueChange={setActiveTab} className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existing">{t('existingGroup')}</TabsTrigger>
              <TabsTrigger value="new">{t('createNewGroup')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="existing" className="mt-4">
              {contractGroups.length > 0 ? (
                <div className="space-y-4">
                  <Select 
                    value={selectedGroupId || ''} 
                    onValueChange={handleGroupSelectionChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('selectAGroup')} />
                    </SelectTrigger>
                    <SelectContent>
                      {contractGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <DialogFooter className="flex justify-end gap-2 mt-6">
                    <DialogClose asChild>
                      <Button variant="outline" onClick={closeGroupDialog}>
                        {t('cancel')}
                      </Button>
                    </DialogClose>
                    <Button onClick={handleAssignToExistingGroup}>
                      {t('assign')}
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('noExistingGroups')}
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setActiveTab("new")}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('createNewGroup')}
                  </Button>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="new" className="mt-4">
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="group-name">{t('groupName')}*</Label>
                  <Input 
                    id="group-name" 
                    type="text" 
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder={t('enterGroupName')}
                    className="w-full"
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="group-description">{t('description')} ({t('optional')})</Label>
                  <Textarea 
                    id="group-description" 
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    placeholder={t('enterGroupDescription')}
                    className="w-full resize-none"
                    rows={3}
                  />
                </div>
                
                <DialogFooter className="flex justify-end gap-2 mt-6">
                  <DialogClose asChild>
                    <Button variant="outline" onClick={closeGroupDialog}>
                      {t('cancel')}
                    </Button>
                  </DialogClose>
                  <Button 
                    onClick={handleCreateAndAssignGroup}
                    disabled={!newGroupName.trim()}
                  >
                    {t('createAndAssign')}
                  </Button>
                </DialogFooter>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
